import { useDataExplorerAppearance } from './appearanceContext';
import { DataExplorerDisplayCard } from './DataExplorerDisplayCard';
import { DataExplorerDisplayMedia } from './DataExplorerDisplayMedia';
import type {
  DataExplorerDisplayItem,
  DataExplorerRecordCardProps,
} from './types';
import { resolveDataExplorerAppearance } from './theme';
import { cn } from './utils';

function DefaultCheckbox<TSource>({
  item,
  selected,
  label,
  className,
  onToggleSelection,
}: {
  item: DataExplorerDisplayItem<TSource>;
  selected: boolean;
  label?: string;
  className?: string;
  onToggleSelection?: (item: DataExplorerDisplayItem<TSource>) => void;
}) {
  if (!onToggleSelection) {
    return null;
  }

  return (
    <input
      type="checkbox"
      checked={selected}
      aria-label={label}
      className={cn('h-4 w-4 shrink-0 accent-primary', className)}
      onChange={() => onToggleSelection(item)}
      onClick={(event) => event.stopPropagation()}
    />
  );
}

function DefaultFavorite<TSource>({
  item,
  active,
  label,
  className,
  onToggleFavorite,
}: {
  item: DataExplorerDisplayItem<TSource>;
  active: boolean;
  label?: string;
  className?: string;
  onToggleFavorite?: (item: DataExplorerDisplayItem<TSource>) => void;
}) {
  if (!onToggleFavorite) {
    return null;
  }

  return (
    <button
      type="button"
      aria-pressed={active}
      title={label}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        active && 'text-[hsl(var(--warning,45_93%_47%))]',
        className,
      )}
      onClick={(event) => {
        event.stopPropagation();
        onToggleFavorite(item);
      }}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill={active ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      >
        <path d="m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8-6.2-3.3L5.8 21 7 14.2 2 9.3l6.9-1L12 2Z" />
      </svg>
      <span className="sr-only">{label}</span>
    </button>
  );
}

function renderKeywords<TSource>({
  item,
  limit,
  classNames,
}: {
  item: DataExplorerDisplayItem<TSource>;
  limit: number;
  classNames?: DataExplorerRecordCardProps<TSource>['classNames'];
}) {
  const keywords = item.keywords ?? [];
  if (keywords.length === 0) {
    return null;
  }

  const visible = keywords.slice(0, limit);
  const rest = Math.max(0, keywords.length - visible.length);

  return (
    <div className={cn('mt-2 flex flex-wrap gap-2', classNames?.keywords)}>
      {visible.map((keyword, index) => (
        <span
          key={index}
          className={cn(
            'max-w-full truncate rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground',
            classNames?.keyword,
          )}
          title={typeof keyword === 'string' ? keyword : undefined}
        >
          {keyword}
        </span>
      ))}
      {rest > 0 ? (
        <span
          className={cn(
            'rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground',
            classNames?.keywordOverflow,
          )}
        >
          +{rest}
        </span>
      ) : null}
    </div>
  );
}

export function DataExplorerRecordCard<TSource = unknown>({
  item,
  selected = item.selected ?? false,
  active,
  disabled = item.disabled,
  hasMedia,
  showHeaderWithMedia = false,
  selectionVisible,
  favoriteVisible,
  overlayControlsAlwaysVisible,
  favoriteActive = false,
  favoriteLabel = 'Favorite',
  favoriteActiveLabel = 'Unfavorite',
  selectLabel = 'Select item',
  unselectLabel = 'Unselect item',
  actions,
  media,
  footer,
  checkbox,
  favorite,
  keywordLimit = 3,
  appearance,
  className,
  classNames,
  onClick,
  onToggleSelection,
  onToggleFavorite,
}: DataExplorerRecordCardProps<TSource>) {
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const resolvedHasMedia = hasMedia ?? Boolean(media ?? item.media);
  const shouldRenderHeader = !resolvedHasMedia || showHeaderWithMedia;
  const shouldShowSelection =
    selectionVisible ?? Boolean(selected || onToggleSelection);
  const shouldShowFavorite =
    favoriteVisible ?? Boolean(favorite || onToggleFavorite);
  const favoriteTitle = favoriteActive ? favoriteActiveLabel : favoriteLabel;
  const selectionLabel = selected ? unselectLabel : selectLabel;
  const renderedSelection =
    checkbox ??
    (shouldShowSelection ? (
      <DefaultCheckbox
        item={item}
        selected={selected}
        label={selectionLabel}
        className={classNames?.selectionControl}
        onToggleSelection={onToggleSelection}
      />
    ) : null);
  const renderedFavorite =
    favorite ??
    (shouldShowFavorite ? (
      <DefaultFavorite
        item={item}
        active={favoriteActive}
        label={favoriteTitle}
        className={classNames?.favoriteControl}
        onToggleFavorite={onToggleFavorite}
      />
    ) : null);
  const renderedMedia =
    media ??
    (item.media ? (
      <DataExplorerDisplayMedia media={item.media} fallbackTitle={item.title} />
    ) : undefined);
  const header = shouldRenderHeader ? (
    <div className={cn('flex flex-col', classNames?.header)}>
      <div
        className={cn(
          'flex min-w-0 items-center justify-between gap-2',
          classNames?.headerMain,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {renderedSelection}
          <div className="min-w-0 flex-1">
            {item.title ? (
              <h3
                className={cn(
                  'truncate text-sm font-semibold leading-none',
                  classNames?.title,
                )}
              >
                {item.title}
              </h3>
            ) : null}
          </div>
        </div>
        {renderedFavorite || actions ? (
          <div className="flex shrink-0 items-center gap-1">
            {renderedFavorite}
            {actions}
          </div>
        ) : null}
      </div>
      {renderKeywords({ item, limit: keywordLimit, classNames })}
    </div>
  ) : null;
  const overlay =
    resolvedHasMedia && (renderedSelection || renderedFavorite || actions) ? (
      <div className={cn('absolute inset-x-2 top-2 z-10', classNames?.overlay)}>
        <div
          className={cn(
            'flex items-start justify-between gap-1 transition-opacity',
            selected || overlayControlsAlwaysVisible
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100',
            classNames?.overlayControls,
          )}
        >
          {renderedSelection ?? <span />}
          <div className="flex items-center gap-1">
            {renderedFavorite}
            {actions ? (
              <div className={cn(classNames?.floatingActions)}>{actions}</div>
            ) : null}
          </div>
        </div>
      </div>
    ) : null;

  return (
    <DataExplorerDisplayCard
      item={item}
      selected={selected}
      disabled={disabled}
      appearance={appearance}
      className={cn(
        theme.slots.recordCardRoot,
        active && theme.slots.recordCardActive,
        classNames?.root,
        className,
      )}
      classNames={{
        overlay: classNames?.overlay,
        header: cn(resolvedHasMedia ? 'p-3' : 'p-3 pb-2', classNames?.header),
        media: cn('p-0 bg-muted/30', classNames?.media),
        footer: classNames?.footer,
      }}
      header={header}
      overlay={overlay}
      media={
        renderedMedia ? (
          <div className={cn('relative overflow-hidden', classNames?.mediaFrame)}>
            {renderedMedia}
          </div>
        ) : undefined
      }
      footer={footer}
      children={null}
      onClick={onClick}
    />
  );
}
